import React, { createContext, useContext, useState, useEffect } from "react";
import { auth, db, googleProvider } from "./firebase";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser]               = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading]         = useState(true);

  // Load user profile from Firestore
  const loadProfile = async (firebaseUser) => {
    if (!firebaseUser) { setUserProfile(null); return; }
    try {
      const ref  = doc(db, "users", firebaseUser.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setUserProfile(snap.data());
      } else {
        const newProfile = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          plan: 'free',
          stripeCustomerId: null,
          subscriptionId: null,
          subscriptionEnd: null,
          createdAt: serverTimestamp(),
        };
        await setDoc(ref, newProfile);
        setUserProfile(newProfile);
      }
    } catch(e) { console.error("Error loading profile:", e); }
  };

  // Check Stripe and sync to Firestore + state
  const syncStripeStatus = async (firebaseUser, profile) => {
    try {
      const customerId = profile?.stripeCustomerId || null;
      const url = `/api/stripe?action=status&userId=${firebaseUser.uid}${customerId ? `&stripeCustomerId=${customerId}` : ''}`;
      const res  = await fetch(url);
      const data = await res.json();
      if (data.isPro) {
        // Update local state immediately for UI — Firestore is updated by webhook
        const end = data.currentPeriodEnd ? new Date(data.currentPeriodEnd * 1000) : null;
        setUserProfile(prev => ({ ...prev, plan: "pro", subscriptionEnd: end }));
      } else if (profile?.plan === "pro") {
        // Stripe says not pro — update local state; webhook handles Firestore
        setUserProfile(prev => ({ ...prev, plan: "free", subscriptionEnd: null }));
      }
      return data;
    } catch(e) { console.error("Stripe sync error:", e); return {}; }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (!firebaseUser) { setUserProfile(null); setLoading(false); return; }

      await loadProfile(firebaseUser);

      // Get latest profile after load to pass stripeCustomerId to status check
      const ref  = doc(db, "users", firebaseUser.uid);
      const snap = await getDoc(ref).catch(() => null);
      const profile = snap?.data() || null;

      if (window.location.search.includes('upgraded=true')) {
        // Returning from Stripe — sync immediately and clean URL
        await syncStripeStatus(firebaseUser, profile);
        window.history.replaceState({}, '', window.location.pathname);
      } else if (profile?.plan !== 'pro') {
        // Not pro in Firestore — silently verify with Stripe in background
        syncStripeStatus(firebaseUser, profile);
      }

      setLoading(false);
    });
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signInWithGoogle = async () => {
    try {
      googleProvider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, googleProvider);
    }
    catch(e) { console.error("Sign in error:", e); }
  };

  const logout = async () => {
    try { await signOut(auth); setUser(null); setUserProfile(null); }
    catch(e) { console.error("Sign out error:", e); }
  };

  const isPro = () => {
    if (!userProfile || userProfile.plan !== "pro") return false;
    if (!userProfile.subscriptionEnd) return true;
    try {
      const end = userProfile.subscriptionEnd?.toDate
        ? userProfile.subscriptionEnd.toDate()
        : userProfile.subscriptionEnd instanceof Date
        ? userProfile.subscriptionEnd
        : new Date(userProfile.subscriptionEnd);
      return end > new Date();
    } catch(e) { return true; }
  };

  const isLoggedIn = () => !!user;

  return (
    <AuthContext.Provider value={{ user, userProfile, loading, signInWithGoogle, logout, isPro, isLoggedIn }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);